<template>
  <component
    :is="tag"
    class="heading"
    :class="[{ billboard: billboard }, tag, align]"
  >
    <span
      v-if="icon && !mediaUrl"
      class="centered-icon"
      :class="`color-${color}`"
    >
      <Icon
        :style="iconStyle"
        class="icon"
        :size="iconSize"
        color="primary"
        :name="icon"
      />
    </span>
    <GraphicMedia
      v-else-if="mediaUrl && mediaDisplayType === 'scale_to_fit'"
      v-show="showGraphicMedia && isPaysage"
      class="graphic-media"
      :media="media"
    />
    <span
      v-else-if="
        mediaUrl &&
        mediaDisplayType !== 'center_circle' &&
        !media.doNotForceColorization
      "
      v-show="showGraphicMedia"
      class="centered-icon"
    >
      <GraphicMask
        class="graphic-icon"
        :src="mediaUrl"
        :size="3.5"
        :color="colorIcon"
      />
    </span>
    <span v-else-if="mediaUrl" v-show="showGraphicMedia" class="centered-icon">
      <GraphicIcon
        class="graphic-icon"
        :src="mediaUrl"
        :size="3.5"
        :color="colorIcon"
      />
    </span>
    <slot />
  </component>
</template>
<script lang="ts" setup>
import type { MediaSchema } from '~/types/schema';

interface HeadingProps {
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  align?: 'left' | 'center' | 'right';
  color?: 'primary' | 'neutral-900' | 'secondary' | 'success';
  icon?: string;
  media?: MediaSchema;
  mediaDisplayType?: string;
  mediaCompressed?: boolean;
  categoryContentType?: string;
}
const props = defineProps({
  tag: null,
  align: { default: 'left' },
  color: { default: 'neutral-900' },
  icon: { default: '' },
  media: { default: null },
  mediaDisplayType: { default: '' },
  mediaCompressed: { type: Boolean, default: true },
  categoryContentType: { default: 'default' }
});
const { $colors } = useNuxtApp();
const mediaUrl = computed(() => {
  if (props.media) {
    return props.mediaCompressed ? props.media.urlCompressed : props.media.url;
  }
  return '';
});
const billboard = computed(() => {
  return props.mediaDisplayType && props.mediaDisplayType === 'scale_to_fit';
});
const colorIcon = computed(() => $colors.neutral_700);

const isPaysage = computed(() => {
  if (!props.media) return false;
  const ratio = props.media.width / props.media.height;
  return ratio >= 4 / 3;
});

const showGraphicMedia = computed(() => {
  switch (props.categoryContentType) {
    case ContentTypes.InterDisciplineInfoIntox:
    case ContentTypes.InterDisciplineDailyQuiz:
    case ContentTypes.InterDisciplineActu:
    case ContentTypes.InterDisciplineFocusOn:
    case ContentTypes.InterDisciplineOther:
    case ContentTypes.SubChapter:
      return false;
    default:
      return true;
  }
});
const iconSize = computed(() => {
  switch (props.tag) {
    case 'h1':
      return '3.5rem';
    case 'h2':
      return '3rem';
    case 'h3':
      return '2.6rem';
    case 'h4':
      return '2.8rem';
    case 'h5':
      return '2.0rem';
    case 'h6':
      return '1.8rem';
  }
});
const iconStyle = computed(() => {
  switch (props.tag) {
    case 'h1':
      return {
        marginRight: '1rem',
      };
    case 'h2':
      return {
        marginRight: '1rem',
      };
    case 'h3':
      return {
        marginRight: '1rem',
      };
    case 'h4':
      return {
        marginRight: '0.5rem',
      };
    case 'h5':
      return {
        marginRight: '1rem',
      };
    case 'h6':
      return {
        marginRight: '1rem',
      };
  }
});
</script>
<style stylus scoped>
.centered-icon {
  display: inline-flex;
  align-items: center;
  vertical-align: text-bottom;
}

.heading {
  display: block;
  align-items: center;
  width: 100%;
  &.billboard {
    display: flex;
    flex-direction: column;
    gap: var(--gap_l);
  }
}
.icon {
  position: relative;
}
.center {
  justify-content: center;
  text-align: center;
}
.left {
  justify-content: flex-start;
  text-align: left;
}
.right {
  justify-content: flex-end;
  text-align: right;
}

.graphic-icon {
  display: inline-block;
  width: 2.4rem;
  margin-right: 1rem;
}
.graphic-media {
  display: block;
  width: 100%;
  border-radius: 0.8rem;
}

.color-primary {
  color: var(--color_primary);
}

.color-secondary {
  color: var(--color_secondary);
}

.color-success {
  color: var(--color_success);
}
</style>
